import { useEffect, useRef } from 'react'
import {
  useMidiStore,
  VIRTUAL_KEYBOARD_INPUT,
  VIRTUAL_KEYBOARD_INPUT_ID,
} from '@/stores/midiStore'
import {
  midiMessageColor,
  midiMessageTypeLabel,
  noteNumberToName,
} from '@/lib/midiUtils'

interface MidiMonitorProps {
  /**
   * `modal` keeps the log compact for the editor's floating panel.
   * `page` gives it more vertical space on the dedicated /midi route.
   */
  variant?: 'modal' | 'page'
}

/**
 * Live log of every MIDI message arriving from any input — hardware
 * or the virtual keyboard. Scrolls to the bottom on each new message
 * so the most recent traffic is always visible.
 */
export function MidiMonitor({ variant = 'modal' }: MidiMonitorProps) {
  const messageLog = useMidiStore(s => s.messageLog)
  const clearLog = useMidiStore(s => s.clearLog)
  const selectedHwInputId = useMidiStore(s => s.selectedHwInputId)
  const hwInputs = useMidiStore(s => s.hwInputs)

  const focusedInputName =
    selectedHwInputId === VIRTUAL_KEYBOARD_INPUT_ID
      ? VIRTUAL_KEYBOARD_INPUT.name
      : (hwInputs.find(i => i.id === selectedHwInputId)?.name ?? null)

  const logEndRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messageLog.length])

  const padding = variant === 'page' ? 'p-4' : 'p-3'
  const logHeight = variant === 'page' ? 'h-72' : 'h-48'

  return (
    <section
      className={`bg-surface-raised border border-brd-subtle rounded-xl ${padding}`}
    >
      <header className="flex items-center justify-between mb-2 gap-2">
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-content-tertiary flex items-center gap-2">
          <span>MIDI Monitor</span>
          {focusedInputName && (
            <span className="text-[10px] normal-case tracking-normal text-second font-medium">
              {focusedInputName}
            </span>
          )}
        </h3>
        {messageLog.length > 0 && (
          <button
            type="button"
            onClick={clearLog}
            className="text-[10px] text-content-tertiary hover:text-content-secondary"
          >
            Clear
          </button>
        )}
      </header>
      <div
        className={`${logHeight} overflow-y-auto bg-surface-sunken rounded border border-brd-subtle font-mono text-[10px]`}
      >
        {messageLog.length === 0 ? (
          <div className="flex items-center justify-center h-full text-content-tertiary text-center px-3">
            {selectedHwInputId
              ? 'Waiting for MIDI data...'
              : 'Select an input above to monitor its messages.'}
          </div>
        ) : (
          <div className="p-1">
            {messageLog.map((msg, i) => {
              const time = new Date(msg.timestamp).toISOString().slice(11, 23)
              const label = midiMessageTypeLabel(msg.type)
              const color = midiMessageColor(msg.type)
              let detail = ''
              if (msg.type === 'noteon' || msg.type === 'noteoff') {
                detail = `${noteNumberToName(msg.note!)}  vel=${msg.velocity}`
              } else if (msg.type === 'cc') {
                detail = `#${msg.cc}  val=${msg.value}`
              } else if (msg.type === 'pitchbend') {
                detail = `val=${msg.value}`
              } else if (msg.type === 'programchange') {
                detail = `prog=${msg.value}`
              }
              return (
                <div
                  key={i}
                  className="flex items-center gap-1.5 py-0.5 text-content-secondary"
                >
                  <span className="text-content-tertiary w-20 flex-shrink-0">
                    {time}
                  </span>
                  <span
                    className="px-1 py-0 rounded text-white font-medium w-16 text-center flex-shrink-0"
                    style={{ backgroundColor: color }}
                  >
                    {label}
                  </span>
                  <span
                    className="text-content-tertiary w-8 flex-shrink-0 text-center"
                    title="MIDI channel"
                  >
                    ch{msg.channel}
                  </span>
                  <span className="flex-1 truncate">{detail}</span>
                </div>
              )
            })}
            <div ref={logEndRef} />
          </div>
        )}
      </div>
    </section>
  )
}
